import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form, message } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";
import dayjs from "dayjs";
import { DEBOUNCED_SEARCH_QUERY, PAGE_SIZE, SHORTCUTS } from "../../Constant";
import { useKeyboardShortcut } from "../../Hook";
import {
  useBrandsQuery,
  useBrandsRequest,
} from "../../Services";
import {
  useCategoriesQuery,
  useCategoriesRequest,
} from "../../Services";
import {
  useCreateProductMutation,
  useDeleteProductMutation,
  useImportProductsMutation,
  useProductsQuery,
  useToggleProductNegativeQtyStatusMutation,
  useToggleProductPosStatusMutation,
  useToggleProductStatusMutation,
  useUpdateProductMutation,
} from "../../Services";
import { Product } from "../../Types";
import {
  PRODUCT_IMPORT_MAX_ROWS,
  validateProductImportFile,
} from "./productImportValidation";

export const useProductsPage = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const searchInputRef = useRef<any>(null);

  useKeyboardShortcut(SHORTCUTS.PRODUCTS.FOCUS_SEARCH, () => {
    if (document.activeElement !== searchInputRef.current?.input) {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    }
  });

  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [brandId, setBrandId] = useState<number | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [historyProduct, setHistoryProduct] = useState<Product | null>(null);
  const [form] = Form.useForm();

  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);

  const [importReportOpen, setImportReportOpen] = useState(false);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importSuccessMessage, setImportSuccessMessage] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const productQueryParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      search,
      category_id: categoryId,
      brand_id: brandId,
    }),
    [brandId, categoryId, page, search],
  );

  const {
    data: productsData,
    isLoading,
    refetch,
  } = useProductsQuery(productQueryParams, {
    placeholderData: (previousData) => previousData,
  });

  const { data: categories = [] } = useCategoriesQuery();
  const { data: brands = [] } = useBrandsQuery();
  const categoriesRequest = useCategoriesRequest();
  const brandsRequest = useBrandsRequest();

  const importMutation = useImportProductsMutation();
  const createProductMutation = useCreateProductMutation();
  const updateProductMutation = useUpdateProductMutation();
  const togglePosMutation = useToggleProductPosStatusMutation();
  const toggleNegativeQtyMutation = useToggleProductNegativeQtyStatusMutation();
  const toggleStatusMutation = useToggleProductStatusMutation();
  const deleteProductMutation = useDeleteProductMutation();

  useEffect(() => {
    setProducts(productsData?.data || []);
    setTotal(productsData?.total || 0);
  }, [productsData]);

  const prefetchProductLookups = () => {
    categoriesRequest.mutate();
    brandsRequest.mutate();
  };

  const invalidateProducts = () => {
    queryClient.invalidateQueries({ queryKey: ["products"] });
  };

  const updateProductCache = (
    id: number,
    patch: Partial<Product> & Record<string, any>,
  ) => {
    setProducts((prev) =>
      prev.map((product) =>
        product.id === id ? { ...product, ...patch } : product,
      ),
    );

    queryClient.setQueriesData({ queryKey: ["products"] }, (old: any) => {
      if (!old) return old;

      if (Array.isArray(old)) {
        return old.map((product: any) =>
          product.id === id ? { ...product, ...patch } : product,
        );
      }

      if (old.products) {
        return {
          ...old,
          products: old.products.map((product: any) =>
            product.id === id ? { ...product, ...patch } : product,
          ),
        };
      }

      return old;
    });
  };

  const restoreProducts = (previousProducts: Product[]) => {
    setProducts(previousProducts);

    queryClient.setQueriesData({ queryKey: ["products"] }, (old: any) => {
      if (!old) return old;

      if (Array.isArray(old)) {
        return previousProducts;
      }

      if (old.products) {
        return {
          ...old,
          products: previousProducts,
        };
      }

      return old;
    });
  };

  const triggerImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const importValidation = validateProductImportFile(file);
    if (importValidation.valid === false) {
      message.error(importValidation.message);
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const data = evt.target?.result;
        const XLSX = await import("xlsx");
        const workbook = XLSX.read(data, {
          type: "array",
          cellFormula: false,
          cellHTML: false,
        });
        const sheetName = workbook.SheetNames[0];
        if (!sheetName) {
          message.warning("The selected Excel file does not contain a sheet.");
          return;
        }

        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json<any>(sheet);

        if (jsonData.length === 0) {
          message.warning("The selected Excel file is empty.");
          return;
        }

        if (jsonData.length > PRODUCT_IMPORT_MAX_ROWS) {
          message.error(
            `Product import files can contain up to ${PRODUCT_IMPORT_MAX_ROWS} rows.`,
          );
          return;
        }

        const groupedProductsMap = new Map<string, any>();

        jsonData.forEach((row, idx) => {
          const skuCode = (row["SKU Code"] || "").toString().trim();
          const barcode = (row["Barcode"] || "").toString().trim();
          const title = (row["Product Title"] || "").toString().trim();

          const uniqueKey = skuCode || barcode || title;
          if (!uniqueKey) return;

          const quantity = parseInt(row["Initial Stock Qty"], 10) || 0;
          const costPrice = parseFloat(row["Cost Price"]) || 0;
          const retailPrice = parseFloat(row["Retail Price"]) || 0;
          const wholesalePrice = parseFloat(row["Wholesale Price"]) || 0;

          const batch = {
            batch_no: `BATCH-new-${idx}`,
            quantity,
            cost_price: costPrice,
            retail_price: retailPrice,
            wholesale_price: wholesalePrice,
            is_default: 0,
          };

          if (groupedProductsMap.has(uniqueKey)) {
            const existingProduct = groupedProductsMap.get(uniqueKey);
            existingProduct.stocks.push(batch);
          } else {
            groupedProductsMap.set(uniqueKey, {
              title,
              category_name: (row["Category"] || "").toString().trim(),
              brand_name: (row["Brand"] || "").toString().trim(),
              unit: (row["Unit"] || "Pcs").toString().trim(),
              sku_code: skuCode,
              mrp: parseFloat(row["MRP"]) || 0,
              alert_quantity: parseInt(row["Alert Quantity"], 10) || 10,
              description: (row["Description"] || "").toString().trim(),
              barcode,
              row_no: idx + 1,
              stocks: [batch],
            });
          }
        });

        const mappedProducts = Array.from(groupedProductsMap.values()).map(
          (product) => {
            if (product.stocks.length > 0) {
              product.stocks[0].is_default = 1;
            }
            return product;
          },
        );

        importMutation.mutate(mappedProducts, {
          onSuccess: (res) => {
            if (res?.success) {
              setImportErrors([]);
              setImportSuccessMessage(
                res?.message || "All products imported successfully!",
              );
              setImportReportOpen(true);
            } else if (res?.errors && Array.isArray(res.errors)) {
              setImportErrors(res.errors);
              setImportSuccessMessage("");
              setImportReportOpen(true);
            } else {
              message.error(res?.message || "Import failed.");
            }
          },
          onError: (err: any) => {
            message.error(
              err?.message || "An unexpected error occurred during import.",
            );
          },
        });
      } catch {
        message.error("Failed to parse the Excel file.");
      }
    };
    reader.onerror = () => {
      message.error("Failed to read the selected Excel file.");
    };
    reader.readAsArrayBuffer(file);
  };

  const openDrawer = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      form.setFieldsValue({
        title: product.title,
        category_id: product.category_id,
        brand_id: product.brand_id,
        unit: product.unit,
        sku_code: product.sku_code,
        mrp: product.mrp,
        alert_quantity: product.alert_quantity,
        description: product.description || "",
        barcode: product.barcode,
        suppliers: product.suppliers?.map((supplier) => supplier.id),
        stocks: product.stocks?.map((st) => ({
          ...st,
          expiry_date: st.expiry_date ? dayjs(st.expiry_date) : null,
        })),
        is_negative_qty_allowed: product.is_negative_qty_allowed === 1,
        printer_id: product.printer_id || null,
        store_id: (product as any).store_id,
      });
    } else {
      setEditingProduct(null);
      form.resetFields();
      form.setFieldsValue({
        alert_quantity: 10,
        is_negative_qty_allowed: false,
        printer_id: null,
        stocks: [
          {
            batch_no: "BATCH-new",
            quantity: 0,
            cost_price: 0,
            retail_price: 0,
            wholesale_price: 0,
            is_default: 1,
          },
        ],
      });
    }
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingProduct(null);
    form.resetFields();
  };

  const handleSubmitProduct = () => {
    const formData = (form as any)._formData;

    if (!formData) {
      message.error("Invalid form data.");
      return;
    }

    const mutation = editingProduct
      ? updateProductMutation
      : createProductMutation;

    mutation.mutate(formData, {
      onSuccess: (res) => {
        if (res?.success) {
          message.success(
            editingProduct
              ? "Product updated successfully!"
              : "Product created successfully!",
          );
          closeDrawer();
        } else if (res?.errors) {
          Object.entries(res.errors).forEach(([field, msg]) => {
            if (field.startsWith("stocks_")) {
              const parts = field.split("_");
              const index = parseInt(parts[1], 10);
              const currentStocks = form.getFieldValue("stocks") || [];
              if (currentStocks[index]) {
                message.error(`Error in Batch #${index + 1}: ${msg}`);
              }
            } else {
              form.setFields([{ name: field, errors: [msg as string] }]);
            }
          });
        } else {
          message.error(res?.message || "Operation failed.");
        }
      },
      onError: (err: any) => {
        message.error(err?.message || "An unexpected error occurred.");
      },
    });
  };

  const handleTogglePos = async (id: number, is_pos: 0 | 1) => {
    await queryClient.cancelQueries({ queryKey: ["products"] });
    const previousProducts = products;
    updateProductCache(id, { is_pos });

    togglePosMutation.mutate(
      { id, is_pos },
      {
        onSuccess: (res) => {
          if (res?.success) {
            message.success(
              is_pos === 1
                ? "Product added to POS."
                : "Product removed from POS.",
            );
          } else {
            restoreProducts(previousProducts);
            message.error(res?.message || "Failed to update POS status.");
          }
        },
        onError: (err: any) => {
          restoreProducts(previousProducts);
          message.error(err?.message || "Failed to update POS status.");
        },
        onSettled: invalidateProducts,
      },
    );
  };

  const handleToggleNegativeQty = async (
    id: number,
    is_negative_qty_allowed: 0 | 1,
  ) => {
    await queryClient.cancelQueries({ queryKey: ["products"] });
    const previousProducts = products;
    updateProductCache(id, { is_negative_qty_allowed });

    toggleNegativeQtyMutation.mutate(
      { id, is_negative_qty_allowed },
      {
        onSuccess: (res) => {
          if (res?.success) {
            message.success(
              is_negative_qty_allowed === 1
                ? "Negative quantity allowed."
                : "Negative quantity blocked.",
            );
          } else {
            restoreProducts(previousProducts);
            message.error(
              res?.message || "Failed to update negative quantity status.",
            );
          }
        },
        onError: (err: any) => {
          restoreProducts(previousProducts);
          message.error(
            err?.message || "Failed to update negative quantity status.",
          );
        },
        onSettled: invalidateProducts,
      },
    );
  };

  const handleToggleStatus = async (product: Product) => {
    const status = product.status === "Active" ? "Inactive" : "Active";

    await queryClient.cancelQueries({ queryKey: ["products"] });
    const previousProducts = products;
    updateProductCache(product.id, { status });

    toggleStatusMutation.mutate(
      { id: product.id, status },
      {
        onSuccess: (res) => {
          if (res?.success) {
            message.success(`Product marked as ${status}.`);
          } else {
            restoreProducts(previousProducts);
            message.error(res?.message || "Failed to change status.");
          }
        },
        onError: (err: any) => {
          restoreProducts(previousProducts);
          message.error(err?.message || "Failed to change status.");
        },
        onSettled: invalidateProducts,
      },
    );
  };

  const handleDelete = (id: number) => {
    deleteProductMutation.mutate(id, {
      onSuccess: (res) => {
        if (res?.success) {
          message.success("Product deleted successfully!");
        } else {
          message.error(res?.message || "Failed to delete product.");
        }
      },
      onError: (err: any) => {
        message.error(err?.message || "Failed to delete product.");
      },
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setPage(1);
  };

  const handleCategoryFilter = (val: number | null) => {
    setCategoryId(val);
    setPage(1);
  };

  const handleBrandFilter = (val: number | null) => {
    setBrandId(val);
    setPage(1);
  };

  const closeHistoryDrawer = () => {
    setHistoryDrawerOpen(false);
    setHistoryProduct(null);
  };

  return {
    drawerOpen,
    editingProduct,
    excelSheetUploading: importMutation.isPending,
    fileInputRef,
    form,
    handleFileImport,
    handleSubmitProduct,
    historyDrawerOpen,
    historyProduct,
    importErrors,
    importReportOpen,
    importSuccessMessage,
    isLoading,
    page,
    products,
    setImportReportOpen,
    setPage,
    submitLoading:
      createProductMutation.isPending || updateProductMutation.isPending,
    total,
    triggerImportClick,
    closeDrawer,
    closeHistoryDrawer,
    openDrawer,
    handleTogglePos,
    handleToggleNegativeQty,
    handleToggleStatus,
    handleDelete,
    prefetchProductLookups,
    refetch,
    categories,
    brands,
    categoriesRequest,
    brandsRequest,
    handleCategoryFilter,
    handleBrandFilter,
    searchInputRef,
    searchInput,
    handleSearchChange,
    setSearchInput,
    setHistoryProduct,
    setHistoryDrawerOpen,
  };
};
